import { X } from "lucide-react"
import { type ReactNode, useEffect, useId, useRef } from "react"
import { createPortal } from "react-dom"
import { Z_INDEX } from "../../constants"
import { usePortalContainer } from "../../hooks/usePortalContainer"

interface CompactSheetProps {
    title: string
    onClose: () => void
    children: ReactNode
}

export function CompactSheet({ title, onClose, children }: CompactSheetProps) {
    const portalContainer = usePortalContainer()
    const titleId = useId()
    const closeButtonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const frame = requestAnimationFrame(() => closeButtonRef.current?.focus())
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return
            event.preventDefault()
            onClose()
        }
        window.addEventListener("keydown", onKeyDown)
        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener("keydown", onKeyDown)
        }
    }, [onClose])

    const sheet = (
        <div
            className="fixed inset-0 flex items-end bg-black/40"
            style={{ zIndex: Z_INDEX.PORTAL_SHEET }}
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="flex max-h-[80dvh] w-full flex-col border-t border-border bg-base-100 text-base-content shadow-2xl"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
                <header className="flex min-h-12 shrink-0 items-center justify-between border-b border-border px-4 py-2">
                    <h2 id={titleId} className="text-sm font-medium">
                        {title}
                    </h2>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={onClose}
                        className="btn flex h-10 w-10 items-center justify-center text-muted transition-colors hover:bg-base-200 hover:text-base-content"
                        aria-label={`Close ${title}`}
                    >
                        <X size={18} />
                    </button>
                </header>
                <div className="min-h-0 overflow-y-auto overscroll-contain">{children}</div>
            </section>
        </div>
    )

    return createPortal(sheet, portalContainer ?? document.body)
}
